/*
 * Custom image slider component for floor plan images
 */

import { useState, useEffect } from "react";
import styles from "../floorplans.module.scss";
import defaultImage from "../../../assets/images/no-image.png";

export default function FloorplanImageSlider({ images, altText, onImageError, fallbackImage }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);

  // Sort images by rank if available
  const sortedImages = images?.length > 0 
    ? [...images].sort((a, b) => {
        const rankA = parseInt(a?.image_metadata?.rank || 0);
        const rankB = parseInt(b?.image_metadata?.rank || 0);
        return rankA - rankB;
      })
    : [];

  const hasMultipleImages = sortedImages && sortedImages.length > 1;

  // Keyboard navigation - must be called before any conditional returns
  useEffect(() => {
    if (!hasMultipleImages) {
      return;
    }

    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        setCurrentIndex((prevIndex) =>
          prevIndex === 0 ? sortedImages.length - 1 : prevIndex - 1
        );
        setIsAutoPlaying(false);
      } else if (e.key === "ArrowRight") {
        setCurrentIndex((prevIndex) =>
          prevIndex === sortedImages.length - 1 ? 0 : prevIndex + 1
        );
        setIsAutoPlaying(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasMultipleImages, sortedImages.length]);

  // If no images or only one image, don't show slider
  if (!hasMultipleImages) {
    return (
      <div className={styles.FloorplanImageWrapper}>
        <img
          src={sortedImages?.[0]?.image_url || fallbackImage || defaultImage}
          alt={altText || "Floor plan"}
          onError={onImageError}
        />
      </div>
    );
  }

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? sortedImages.length - 1 : prevIndex - 1
    );
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === sortedImages.length - 1 ? 0 : prevIndex + 1
    );
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  return (
    <div className={styles.FloorplanImageSlider}>
      <div className={styles.FloorplanImageWrapper}>
        {sortedImages.map((image, index) => (
          <div
            key={index}
            className={`${styles.SliderSlide} ${
              index === currentIndex ? styles.active : ""
            }`}
          >
            <img
              src={image?.image_url || defaultImage}
              alt={image?.image_metadata?.caption || altText || `Floor plan ${index + 1}`}
              onError={onImageError}
            />
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {sortedImages.length > 1 && (
        <>
          <button
            className={`${styles.SliderArrow} ${styles.SliderArrowLeft}`}
            onClick={goToPrevious}
            aria-label="Previous image"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
              <path d="M1 1L5 5L9 1" stroke="#201C18" strokeLinecap="square" />
            </svg>
          </button>
          <button
            className={`${styles.SliderArrow} ${styles.SliderArrowRight}`}
            onClick={goToNext}
            aria-label="Next image"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
              <path d="M1 1L5 5L9 1" stroke="#201C18" strokeLinecap="square" />
            </svg>
          </button>
        </>
      )}

      {/* Thumbnail Indicators */}
      {sortedImages.length > 1 && (
        <div className={styles.SliderThumbnails}>
          {sortedImages.map((image, index) => (
            <button
              key={index}
              className={`${styles.SliderThumbnail} ${
                index === currentIndex ? styles.active : ""
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to image ${index + 1}`}
              type="button"
            >
              <img
                src={image?.image_url || defaultImage}
                alt={image?.image_metadata?.caption || `Thumbnail ${index + 1}`}
                onError={onImageError}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

